import React, { useEffect, useMemo, useState } from 'react';

const API = process.env.REACT_APP_API_URL;

// Slide-over panel for compliance officers to grant or revoke CAP-creator
// rights. Flow matches the requirement: pick a department → see its users →
// toggle each user. Saves immediately on toggle (no separate "save" step).
//
// Mounted at the top-right of CapList for compliance users only. Visibility
// is doubly gated — the trigger only renders when the server returns
// can_manage=true, and the endpoints behind every call here are
// compliance-only on the server side. UI gating is convenience; server
// gating is truth.
export default function CapAccessManager({ open, onClose }) {
  const [departments, setDepartments] = useState([]);
  const [grants, setGrants] = useState([]);
  const [selectedDept, setSelectedDept] = useState('');
  const [deptUsers, setDeptUsers] = useState([]);
  const [loadingDepts, setLoadingDepts] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [busyUserId, setBusyUserId] = useState(null);
  const [error, setError] = useState(null);

  // Load departments + existing grants whenever the panel opens.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setLoadingDepts(true);
    Promise.all([
      fetch(`${API}/api/pcap/departments`, { credentials: 'include' }).then(r => r.json()),
      fetch(`${API}/api/pcap/access/grants`, { credentials: 'include' }).then(r => r.json()),
    ])
      .then(([d, g]) => {
        if (d?.success) setDepartments(d.departments || []);
        if (g?.success) setGrants(g.grants || []);
      })
      .catch(err => setError(err?.message || 'Failed to load'))
      .finally(() => setLoadingDepts(false));
  }, [open]);

  // Load users when a department is picked.
  useEffect(() => {
    if (!open || !selectedDept) { setDeptUsers([]); return; }
    let cancelled = false;
    setLoadingUsers(true);
    fetch(`${API}/api/pcap/access/department-users?department_id=${encodeURIComponent(selectedDept)}`, {
      credentials: 'include',
    })
      .then(r => r.json())
      .then(d => {
        if (cancelled) return;
        if (d?.success) setDeptUsers(d.users || []);
        else setError(d?.message || 'Failed to load users');
      })
      .catch(err => !cancelled && setError(err?.message || 'Failed to load users'))
      .finally(() => !cancelled && setLoadingUsers(false));
    return () => { cancelled = true; };
  }, [open, selectedDept]);

  const refreshGrants = async () => {
    try {
      const r = await fetch(`${API}/api/pcap/access/grants`, { credentials: 'include' });
      const d = await r.json();
      if (d?.success) setGrants(d.grants || []);
    } catch {}
  };

  const grantsByUserId = useMemo(() => {
    const m = new Map();
    for (const g of grants) m.set(g.user_id, g);
    return m;
  }, [grants]);

  const toggle = async (user) => {
    if (busyUserId) return;
    setBusyUserId(user.id);
    setError(null);
    try {
      const existing = grantsByUserId.get(user.id);
      if (existing) {
        const r = await fetch(`${API}/api/pcap/access/grants/${existing.id}`, {
          method: 'DELETE',
          credentials: 'include',
        });
        const d = await r.json();
        if (!d?.success) throw new Error(d?.message || 'Revoke failed');
      } else {
        const r = await fetch(`${API}/api/pcap/access/grants`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: user.id }),
        });
        const d = await r.json();
        if (!d?.success) throw new Error(d?.message || 'Grant failed');
      }
      setDeptUsers(prev => prev.map(u => u.id === user.id ? { ...u, has_grant: !u.has_grant } : u));
      refreshGrants();
    } catch (err) {
      setError(err?.message || 'Operation failed');
    } finally {
      setBusyUserId(null);
    }
  };

  if (!open) return null;

  return (
    <>
      <div
        onClick={onClose}
        className="fixed inset-0 bg-slate-900/30 backdrop-blur-[1px] z-40"
        aria-hidden="true"
      />
      <aside
        role="dialog"
        aria-label="Manage CAP creation access"
        className="fixed top-0 right-0 h-full w-[460px] max-w-[95vw] bg-white shadow-2xl z-50 flex flex-col"
      >
        <header className="px-6 py-5 border-b border-slate-100 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-[17px] font-semibold text-slate-900 tracking-tight">Manage CAP creation access</h2>
            <p className="text-[13px] text-slate-500 mt-1 leading-snug">
              Pick a department, then grant or revoke CAP-creation rights for people in it. Compliance always has access.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 transition p-1 -mr-1"
            aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M6 18L18 6" />
            </svg>
          </button>
        </header>

        <div className="px-6 py-4 border-b border-slate-100">
          <label className="block text-[12px] font-medium text-slate-500 mb-1.5">Department</label>
          <select
            value={selectedDept}
            onChange={e => setSelectedDept(e.target.value)}
            disabled={loadingDepts}
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 disabled:opacity-50"
          >
            <option value="">{loadingDepts ? 'Loading…' : 'Select a department'}</option>
            {departments.map(d => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        </div>

        <div className="flex-1 overflow-y-auto">
          {error && (
            <div className="mx-6 mt-4 px-3 py-2 rounded-lg bg-red-50 text-red-800 text-[13px] ring-1 ring-red-200">
              {error}
            </div>
          )}

          {!selectedDept && (
            <div className="px-6 py-10 text-center text-[13px] text-slate-400">
              Pick a department to see the people in it.
            </div>
          )}

          {selectedDept && loadingUsers && (
            <div className="px-6 py-10 text-center text-[13px] text-slate-400">Loading…</div>
          )}

          {selectedDept && !loadingUsers && deptUsers.length === 0 && (
            <div className="px-6 py-10 text-center text-[13px] text-slate-400">
              No users in this department.
            </div>
          )}

          {selectedDept && !loadingUsers && deptUsers.length > 0 && (
            <ul className="divide-y divide-slate-100">
              {deptUsers.map(u => {
                const active = u.has_grant;
                const busy = busyUserId === u.id;
                return (
                  <li key={u.id} className="px-6 py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[14px] font-medium text-slate-900 truncate">{u.name || u.email}</div>
                      {u.name && <div className="text-[12px] text-slate-500 truncate">{u.email}</div>}
                    </div>
                    <button
                      onClick={() => toggle(u)}
                      disabled={busy}
                      className={`shrink-0 inline-flex items-center px-3 py-1.5 rounded-lg text-[12px] font-medium transition ring-1 ${
                        active
                          ? 'bg-emerald-50 text-emerald-800 ring-emerald-200 hover:bg-emerald-100'
                          : 'bg-white text-slate-700 ring-slate-200 hover:bg-slate-50'
                      } disabled:opacity-50 disabled:cursor-wait`}
                    >
                      {busy ? '…' : active ? 'Granted — click to revoke' : 'Grant access'}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {grants.length > 0 && (
            <div className="px-6 pt-6 pb-8 border-t border-slate-100 mt-4">
              <h3 className="text-[12px] font-semibold text-slate-500 uppercase tracking-wide mb-3">
                Currently granted ({grants.length})
              </h3>
              <ul className="space-y-1.5">
                {grants.map(g => (
                  <li key={g.id} className="flex items-center justify-between gap-3 text-[13px]">
                    <span className="min-w-0 truncate text-slate-700">
                      {g.user_name || g.user_email}
                      {g.department_name && (
                        <span className="text-slate-400"> — {g.department_name}</span>
                      )}
                    </span>
                    <button
                      onClick={async () => {
                        if (busyUserId) return;
                        setBusyUserId(g.user_id);
                        setError(null);
                        try {
                          const r = await fetch(`${API}/api/pcap/access/grants/${g.id}`, {
                            method: 'DELETE', credentials: 'include',
                          });
                          const d = await r.json();
                          if (!d?.success) throw new Error(d?.message || 'Revoke failed');
                          await refreshGrants();
                          if (selectedDept) {
                            setDeptUsers(prev => prev.map(u => u.id === g.user_id ? { ...u, has_grant: false } : u));
                          }
                        } catch (err) {
                          setError(err?.message || 'Revoke failed');
                        } finally {
                          setBusyUserId(null);
                        }
                      }}
                      className="text-[12px] text-slate-500 hover:text-red-700 transition"
                    >
                      Revoke
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
